import _ from 'lodash';
import { useMemo, useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { convertToMomentJs, eventTypeConvert } from '@/shared/lib/aiUtils';
import { useMergeState } from '@/shared/lib/customHooks';
import {
  selectAiChannel,
  selectSelectedColumnInfo,
  selectSelectedEventIndex,
  useReportStore,
} from '@/entities/report';
import { calculateHrInfoByDate } from '@/entities/heart-rate';
import { isAllowSetEcgDisclosure } from '../handler';
import type { SelectedInfoProps } from './index';

/**
 * Supplies the selected-events panel of the strip tab with its grouped event list, lead options and ECG disclosure controls. Filters the channel options against the ECG data map, groups the selected column entries by converted event type, and confirms a one-hour disclosure window whose heart-rate summary is recomputed before it is stored.
 *
 * @param props - The panel props carrying the ECG data map and the study timezone offset.
 * @returns An object carrying the selected column info, channel options, modal state and the disclosure handlers.
 */
const useSelectedInfo = ({ ecgDataMap, timezoneOffset }: SelectedInfoProps) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const ecgDisclosure = useReportStore((s) => s.selectedEcgDisclosure);
  const setEcgDisclosureChange = useReportStore((s) => s.actions.setEcgDisclosureChange);
  const setIsOpenInvalidTimeModal = useReportStore((s) => s.actions.setIsOpenInvalidTimeModal);
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const setSelectedEventIndexAction = useReportStore((s) => s.actions.setSelectedEventIndex);
  /**
   * Writes the selected event index of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new event index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedEventIndex = (v: Parameters<typeof setSelectedEventIndexAction>[1]) =>
    setSelectedEventIndexAction(keyRecoil, v);
  const ecgDisclosureChange = useReportStore((s) => s.ecgDisclosureChange);

  const selectedEventToSetEcgDisclosure = useRef<any>();

  const selectedEcgDisclosure = useMemo(() => {
    if (!_.isEmpty(ecgDisclosureChange)) return ecgDisclosureChange;
    return ecgDisclosure;
  }, [ecgDisclosure, ecgDisclosureChange]);

  const channelOptions = _.filter(
    [
      {
        label: 'CH1',
        value: 1,
      },
      {
        label: 'CH2',
        value: 2,
      },
      {
        label: 'CH3',
        value: 3,
      },
    ],
    (x) => _.some(ecgDataMap.channels, (y) => y === x.label),
  );

  /**
   * Picks the default ECG disclosure channel for the event being assigned. Returns channel 2 when all three leads are available and channel 1 otherwise.
   *
   * @returns The default disclosure channel number.
   */
  const getEcgDisclosureChannel = () => (channelOptions?.length === 3 ? 2 : 1);

  const [state, setState] = useMergeState({
    isOpenConfirmModal: false,
    ecgDisclosureChannel: getEcgDisclosureChannel(),
  });

  /**
   * Starts an ECG disclosure assignment for one entry of the selected column. Stores that entry in a ref and opens the confirmation modal preset to the default channel derived from the available leads.
   *
   * @param index - Position of the entry within the selected column info list.
   * @returns Nothing.
   */
  const onClickSetEcgDisclosure = (index: any) => {
    selectedEventToSetEcgDisclosure.current = selectedColumnInfo[index];
    setState({
      isOpenConfirmModal: true,
      ecgDisclosureChannel: getEcgDisclosureChannel(),
    });
  };

  /**
   * Abandons the pending ECG disclosure assignment. Clears the held event ref and closes the confirmation modal.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    selectedEventToSetEcgDisclosure.current = undefined;
    setState({ isOpenConfirmModal: false });
  };

  /**
   * Confirms the pending ECG disclosure for the held event. Builds a one-hour window from the event start time and, when the ECG data map allows it, recomputes the heart-rate info for that window and stores the updated disclosure, otherwise opens the invalid-time modal.
   *
   * @returns Nothing.
   */
  const onClickSet = () => {
    const event = selectedEventToSetEcgDisclosure.current;
    const startMoment = convertToMomentJs(event?.startTimeEpoch, timezoneOffset);
    const stopMoment = startMoment.clone().add(1, 'hours');
    const startMomentString = startMoment.toISOString();
    const stopMomentString = stopMoment.toISOString();
    if (
      isAllowSetEcgDisclosure(ecgDataMap, {
        start: startMomentString,
        stop: stopMomentString,
      })
    ) {
      const clonedSelectedEcgDisclosure = _.cloneDeep(selectedEcgDisclosure);
      clonedSelectedEcgDisclosure.start = startMomentString;
      clonedSelectedEcgDisclosure.stop = stopMomentString;
      clonedSelectedEcgDisclosure.channel = state.ecgDisclosureChannel;
      const hrInfo = calculateHrInfoByDate({
        ecgDataMap,
        start: convertToMomentJs(clonedSelectedEcgDisclosure?.start, timezoneOffset),
        stop: convertToMomentJs(clonedSelectedEcgDisclosure?.stop, timezoneOffset),
      });
      _.assign(clonedSelectedEcgDisclosure, hrInfo);
      setEcgDisclosureChange(clonedSelectedEcgDisclosure);
      setState({ isOpenConfirmModal: false });
    } else {
      setState({ isOpenConfirmModal: false });
      setIsOpenInvalidTimeModal(true);
    }
    selectedEventToSetEcgDisclosure.current = undefined;
  };

  const formatSelectedColumnInfo: any = useMemo(() => {
    let eventIndex = 0;
    return _.reduce(
      selectedColumnInfo,
      (result: any, x: any) => {
        const matchObj =
          result[x?.type ? (eventTypeConvert as any)[x.type || x.oldType] : 'Minute info'] ||
          (result[x?.type ? (eventTypeConvert as any)[x.type || x.oldType] : 'Minute info'] = {
            events: [],
            eventIndexes: [],
          });
        matchObj.events.push(x);
        matchObj.eventIndexes.push(eventIndex);
        eventIndex += 1;
        return result;
      },
      {} as any,
    );
  }, [selectedColumnInfo]);

  return {
    selectedColumnInfo,
    aiChannel,
    selectedEventIndex,
    setSelectedEventIndex,
    channelOptions,
    state,
    setState,
    onClickSetEcgDisclosure,
    onClickCancel,
    onClickSet,
    formatSelectedColumnInfo,
  };
};

export default useSelectedInfo;
